import React, { useEffect, useRef, useState, useCallback } from 'react';
import ScriptDialog from 'app/fuse-layouts/shared-components/ScriptDialog';
import OwnerSelectionCard from 'app/shared-components/OwnerSelectionCard';
import { GlobalProvisionScriptsPreview, ClusterApplicationsSection } from '../components';
import {
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	DialogContent,
	DialogTitle,
	Divider,
	Fab,
	Icon,
	IconButton,
	InputBase,
	MenuItem,
	Paper,
	TextField,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Checkbox
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

import clsx from 'clsx';
import Formsy from 'formsy-react';
import { FuseAnimate, SelectFormsy, TextFieldFormsy } from '@fuse';
import * as StackActions from 'app/main/stacks/store/actions/stacks.actions.js';
import { useForm } from '@fuse/hooks';
import _ from '@lodash';
import { useDispatch, useSelector } from 'react-redux';
import * as ActionsDialog from 'app/store/actions';
import * as Actions from '../store/actions';
import { ipValid, validationInputUlt, validationMessUlt } from '../../util/InputValidate';
import useStyles from 'app/cluster-styles/useStyles';
import { getClusterSettings } from 'app/main/settings/store/actions';
import { CLUSTER_STATES, DisabledFormWrapper } from 'app/main/util/clusterUtils';
import { RESOURCE_MANAGER, IMAGE_MODE } from 'app/main/util/enums';
import { getRegionsGroupedByContinent, getRegionLabel, findRegionByCode, regionToLocation } from './maasRegions';

const MaasCluster = props => {
	// initialize
	const dispatch = useDispatch();
	const classes = useStyles(props);
	const formRef = useRef(null);
	const pkgRef = useRef(null);

	// connect to Redux
	const clusterSettings = useSelector(({ settings }) => settings.settings.clusterSettings);
	const cluster = useSelector(({ clusters }) => clusters.cluster.cluster);
	const clustersArr = useSelector(({ clusters }) => clusters.cluster.list);
	const error = useSelector(({ clusters }) => clusters.cluster.error);
	const providers = useSelector(({ providers }) => providers.providers.list);
	const loading = useSelector(({ fuse }) => fuse.settings.loading);
	const credentials = useSelector(({ credentials }) => credentials.credential.list);
	const supportedOSs = useSelector(({ clusters }) => clusters.cluster.os_types);
	const acmLayers = useSelector(({ stacks }) => stacks.stack.acm_images);
	const acmShapes = useSelector(({ stacks }) => stacks.stack.acm_shapes);
	const currentUser = useSelector(({ auth }) => auth.user);
	const preconfiguredDevices = useSelector(({ clusters }) => clusters.cluster.preconfiguredDevices);

	//this is for the prefix form
	const [inputValue, setInputValue] = useState('');
	const [groupNodeInputValues, setGroupNodeInputValues] = useState({});
	const [advanced, setAdvanced] = useState(false);

	// local state
	const [isFormValid, setIsFormValid] = useState(false);
	const [zones, setZones] = useState([]);
	const [regions, setRegions] = useState([]);
	const [nodeInstanceSizes, setNodeInstanceSizes] = useState(null);
	const [serverInstanceSizes, setServerInstanceSizes] = useState([]);
	const [filteredCredentials, setFilteredCredentials] = useState([]);
	const [deletedPkgs, setDeletedPkgs] = useState([]);
	const [clusterNames, setClusterNames] = useState(null)
	const [selectedOwner, setSelectedOwner] = useState('');
	const [imageMode, setImageMode] = useState(IMAGE_MODE.MULTI_IMAGE);

	const [prefixError, setPrefixError] = useState("");
	const [nodePrefixErrors, setNodePrefixErrors] = useState([]);

	//quotas
	const [usedIntances, setUsedIntances] = useState(0);
	const [usedNetwork, setUsedNetwork] = useState(0);
	const [usedCpus, setUsedCpus] = useState(0);

	const [limitIntances, setLimitIntances] = useState(9999999);
	const [limitNetwork, setLimitNetwork] = useState(9999999);
	const [limitCpus, setLimitCpus] = useState(9999999);
	const [headNodeImages, setHeadNodeImages] = useState([]);
	const [computeNodeImges, setComputeNodeImages] = useState([]);
	const [nodeShapes, setNodeShapes] = useState([]);
	const [selectedHeadNodeShape, setSelectedHeadNodeShape] = useState(null);
	const [isVncTokenAuthentication, setIsVncTokenAuthentication] = useState(false);
	const [isVncRemoteClient, setIsVncRemoteClient] = useState(false);
	const [disableInputs, setDisableInputs] = useState(false);
    const [isScriptOpen, setScriptOpen] = useState(false);
	const [excludedGlobalScripts, setExcludedGlobalScripts] = useState([]);
	// Cluster applications state (Feature: cluster-apps)
	const [clusterApplications, setClusterApplications] = useState([]);
	
	// Preconfigured device state (generic - works for any pre-provisioned infrastructure)
	const [selectedDeviceFamily, setSelectedDeviceFamily] = useState(null);
	const [selectedPreconfiguredDevice, setSelectedPreconfiguredDevice] = useState(null);
	const [deviceType, setDeviceType] = useState('standard');
	
	// Commissioned compute nodes - array of selected machine IDs for compute nodes
	const [selectedCommissionedComputeNodes, setSelectedCommissionedComputeNodes] = useState([]);
	
	// Geographic location state for WorldMap visualization
	const [selectedRegion, setSelectedRegion] = useState(null);
	
	// custom hook
	const { form, handleChange, setForm } = useForm(props.oldForm || null);

	// Initialize selectedRegion from form when editing
	useEffect(() => {
		if (props.type === 'edit' && form && form.location && form.location.region && !selectedRegion) {
			const regionFromForm = findRegionByCode(form.location.region);
			if (regionFromForm) {
				setSelectedRegion(regionFromForm);
			}
		}
	}, [props.type, form, selectedRegion]);

	useEffect(() => {
		if(props.type === 'edit' && form && form.info && form.info.state != CLUSTER_STATES.DESTROYED) {
			setDisableInputs(true);
		}
	}, [props, form])

	// Load excluded global scripts when editing
	useEffect(() => {
		if (props.type === 'edit' && form) {
			// Check both locations - data.excludedGlobalScripts and root level excludedGlobalScripts
			const excluded = form.data?.excludedGlobalScripts || form.excludedGlobalScripts;
			if (excluded && excluded.length > 0) {
				setExcludedGlobalScripts(excluded);
			}
		}
	}, [props.type, form?.data?.excludedGlobalScripts, form?.excludedGlobalScripts]);

	// Load cluster applications when editing (Feature: cluster-apps)
	useEffect(() => {
		if (props.type === 'edit' && form) {
			const apps = form.applications;
			if (apps && apps.length > 0) {
				setClusterApplications(apps);
			}
		}
	}, [props.type, form?.applications]);


	useEffect(() => {
		dispatch(StackActions.getStacks());
		dispatch(StackActions.getAcmImages());
		dispatch(StackActions.getAcmShapes());
		dispatch(getClusterSettings());
		dispatch(Actions.getPreconfiguredDevices());
	}, [dispatch]);

	// Detect device family from form data
	useEffect(() => {
		if (form && form.data && form.data.server && form.data.server.machine) {
			const machineType = form.data.server.machine;
			
			// Check if this is a preconfigured device family
			const deviceFamily = preconfiguredDevices.find(df => df.machine_type === machineType);
			
			if (deviceFamily) {
				setDeviceType(machineType);
				setSelectedDeviceFamily(deviceFamily);
			} else if (machineType) {
				// Only set to standard if machine type exists and is not preconfigured
				setDeviceType('standard');
				setSelectedDeviceFamily(null);
			}
			// If machine is empty/undefined, keep the default 'standard' from useState
		}
	}, [form, preconfiguredDevices]);

	// Load selectedPreconfiguredDevice when editing a cluster with commissionedMachineId
	useEffect(() => {
		if (props.type === 'edit' && form && form.commissionedMachineId && preconfiguredDevices && preconfiguredDevices.length > 0) {
			// Find the commissioned machine in the preconfigured devices list
			var machineId = form.commissionedMachineId;
			// Handle ObjectId format from MongoDB (can be { $oid: "..." } or just a string)
			if (machineId && typeof machineId === 'object') {
				if (machineId.$oid) {
					machineId = machineId.$oid;
				} else if (machineId.toString) {
					machineId = machineId.toString();
				}
			}
			
			// Search through all device families to find the matching device
			for (var i = 0; i < preconfiguredDevices.length; i++) {
				var family = preconfiguredDevices[i];
				if (family.devices && family.devices.length > 0) {
					var matchingDevice = family.devices.find(function(d) {
						var deviceId = d.id || d._id;
						if (deviceId && typeof deviceId === 'object') {
							if (deviceId.$oid) {
								deviceId = deviceId.$oid;
							} else if (deviceId.toString) {
								deviceId = deviceId.toString();
							}
						}
						return deviceId === machineId;
					});
					if (matchingDevice) {
						setSelectedDeviceFamily(family);
						setSelectedPreconfiguredDevice(matchingDevice);
						setDeviceType(family.machine_type);
						break;
					}
				}
			}
		}
		
		// Load commissioned compute nodes when editing
		if (props.type === 'edit' && form && form.commissionedComputeNodes && form.commissionedComputeNodes.length > 0) {
			// Convert ObjectIds to strings if needed
			var computeNodeIds = form.commissionedComputeNodes.map(function(nodeId) {
				if (nodeId && typeof nodeId === 'object') {
					if (nodeId.$oid) return nodeId.$oid;
					if (nodeId.toString) return nodeId.toString();
				}
				return nodeId;
			});
			setSelectedCommissionedComputeNodes(computeNodeIds);
		}
	}, [props.type, form, preconfiguredDevices]);

    useEffect(() => {
        const setVncValues = () => {
            if (form && form.info) {
                setIsVncTokenAuthentication(form.info.vncTokenAuthentication !== undefined ? form.info.vncTokenAuthentication : clusterSettings?.vncTokenAuthentication);
                setIsVncRemoteClient(form.info.vncRemoteClient !== undefined ? form.info.vncRemoteClient : clusterSettings?.vncRemoteClient);
            } else if (clusterSettings) {
                setIsVncTokenAuthentication(clusterSettings.vncTokenAuthentication);
                setIsVncRemoteClient(clusterSettings.vncRemoteClient);
            }
        };

        setVncValues();
    }, [form, clusterSettings]);

    // Initialize selectedOwner when editing an existing cluster
    useEffect(() => {
        if (props.type === 'edit' && form && form.user && form.user.length > 0) {
            setSelectedOwner(form.user[0]);
        }
    }, [props.type, form]);


	useEffect(() => {
		if (acmLayers.length > 0) {
			const availableImages = acmLayers.filter(layer => {
				return layer.status === 'available' &&
				layer.type === 'oddc_base' 
			}
				
			);
			const headNodeImages = availableImages
				.filter(layer => layer.image_type === 'server')
				.map(layer => ({ name: layer.name }));

			const computeNodeImages = availableImages
				.filter(layer => layer.image_type === 'node')
				.map(layer => ({ name: layer.name }));

			setHeadNodeImages(headNodeImages);
			setComputeNodeImages(computeNodeImages);
		}
	}, [acmLayers]);

	useEffect(() => {
		if (acmShapes && acmShapes.length > 0) {
			// use for showing only inventory available shapes
			const shapes = acmShapes.map(shape => {
				return {
					name: shape.description,
					instance_id: shape.name,
					machines: shape.machine_names,
					all_machines: shape.all_machine_names
				};
			});
			setNodeShapes(shapes);
		}
	}, [acmShapes]);

	useEffect(() => {
		// Initialize imageMode from form data when editing
		if (form && form.info && form.info.imageMode) {
			setImageMode(form.info.imageMode);
		}
	}, [form && form.info && form.info.imageMode]);

	const handleHeadNodeShapeChange = (event, newValue) => {
		if (newValue) {
			setSelectedHeadNodeShape(newValue);
			const selectedComputeNodeMachines = form.data.node.group.reduce((acc, group) => {
				if (Array.isArray(group.machines)) {
				  return acc.concat(group.machines);
				}
				return acc;
			  }, []);
			  const availableHeadNodeMachines = newValue.all_machines.filter(
				(machine) => !selectedComputeNodeMachines.includes(machine)
			  );
			setForm(prevForm => {
				const updatedForm = {
					...prevForm,
					data: {
						...prevForm.data,
						server: {
							...prevForm.data.server,
							instance_id: newValue.instance_id,
							machine: availableHeadNodeMachines.length > 0 ? availableHeadNodeMachines[0] : '',
						}
					}
				};

				const updatedNodeGroups = updatedForm.data.node.group.map((group, index) => {
					if (group.selectedNodeShape && group.selectedNodeShape.all_machines) {
						const filteredMachines = getFilteredNodeMachines(group.selectedNodeShape.all_machines, index);
						return {
							...group,
							machines: filteredMachines.slice(0, group.count || 0),
							count: Math.min(group.count, filteredMachines.length),
						};
					}
					return group;
				});
				return {
					...updatedForm,
					data: {
						...updatedForm.data,
						node: {
							...updatedForm.data.node,
							group: updatedNodeGroups,
						},
					},
				};
			});
		} else {
			setSelectedHeadNodeShape(null);
			setForm(prevForm => ({
				...prevForm,
				data: {
					...prevForm.data,
					server: {
						...prevForm.data.server,
						instance_id: '',
						machine: ''
					}
				}
			}));
		}
	};

	const handleNodeShapeChange = (index, newValue) => {
		setForm(prevForm => {
			const updatedGroup = [...prevForm.data.node.group];

			if (newValue) {
				const filteredMachines = getFilteredNodeMachines(newValue.all_machines, index);

				updatedGroup[index].instance_id = newValue.instance_id;
				updatedGroup[index].machines = filteredMachines.slice(0, updatedGroup[index].count || 0); // Update machines based on count
				updatedGroup[index].count = Math.min(updatedGroup[index].count || 0, filteredMachines.length); // Ensure count does not exceed available machines
				updatedGroup[index].selectedNodeShape = newValue;
			} else {
				updatedGroup[index].instance_id = '';
				updatedGroup[index].machines = [];
				updatedGroup[index].count = 0;
				updatedGroup[index].selectedNodeShape = null;
			}

			return {
				...prevForm,
				data: {
					...prevForm.data,
					node: {
						...prevForm.data.node,
						group: updatedGroup
					}
				}
			};
		});
	};

	//nodes pre sellection on editing
	useEffect(() => {
		if (form && form.data && form.data.node && form.data.node.group) {
			form.data.node.group.forEach((node, i) => {
				const nodeShape = nodeShapes.find(shape => shape.instance_id === node.instance_id);
				if (nodeShape) {
					setForm(prevForm => ({
						...prevForm,
						data: {
							...prevForm.data,
							node: {
								...prevForm.data.node,
								group: prevForm.data.node.group.map((group, index) =>
									index === i
										? {
											...group,
											selectedNodeShape: nodeShape,
											machines: node.machines || [],
											count: node.machines ? node.machines.length : 0,
										}
										: group
								),
							},
						},
					}));
				}
			});
		}
	}, [nodeShapes, form]);

	//headnode pre sellection on editing
	useEffect(() => {
		if (form && form.data && form.data.server && form.data.server.instance_id) {
			const headNodeShape = nodeShapes.find(shape => shape.instance_id === form.data.server.instance_id);
			if (headNodeShape) {
				setSelectedHeadNodeShape(headNodeShape);

				// Ensure the machine is set if not already set
				if (!form.data.server.machine && headNodeShape.all_machines.length > 0) {
					setForm(prevForm => ({
						...prevForm,
						data: {
							...prevForm.data,
							server: {
								...prevForm.data.server,
								machine: form.data.server.machine || headNodeShape.all_machines[0]
							}
						}
					}));
				}
			}
		}
	}, [form, nodeShapes]);

	const getFilteredNodeMachines = (allMachines = [], currentGroupIndex) => {
		if (!Array.isArray(allMachines)) return [];

		const selectedHeadMachine = form.data.server.machine || '';

		const machinesSelectedInOtherGroups = form.data.node.group
			.filter((_, index) => index !== currentGroupIndex)
			.reduce((acc, group) => [...acc, ...(group.machines || [])], []);

		const machinesSelectedByCurrentGroup = form.data.node.group[currentGroupIndex]?.machines || [];

		return allMachines.filter(
			machine =>
				machine !== selectedHeadMachine &&
				(!machinesSelectedInOtherGroups.includes(machine) || machinesSelectedByCurrentGroup.includes(machine))
		);
	};

	const getAvailableMachines = () => {
		if (!form?.data?.server?.machine || !Array.isArray(form?.data?.node?.group)) {
		  return nodeShapes.filter(shape => shape.machines.length > 0);
		}
		const selectedHeadMachine = form.data.server.machine;
		const selectedComputeNodeMachines = form.data.node.group.reduce((acc, group) => {
		  if (Array.isArray(group.machines)) {
			return acc.concat(group.machines);
		  }
		  return acc;
		}, []);
		const machinesToExclude = [...selectedComputeNodeMachines, selectedHeadMachine];
		const filteredNodeShapes = nodeShapes
		  .filter(shape => shape.machines.length > 0)
		  .map(shape => ({
			...shape,
			machines: shape.machines.filter(machine => !machinesToExclude.includes(machine))
		  }))
		  .filter(shape => shape.machines.length > 0);
	  
		return filteredNodeShapes;
	  };
	useEffect(() => {
		const names = clustersArr.map(cluster => cluster.name);
		setClusterNames(names);
	}, [clustersArr, setClusterNames]);

	const setClusterInfo = (e) => {
		setForm(_.set({ ...form }, 'data', { ...form.data, moab: { ...form.data.moab, [e.target.name]: e.target.value } }));
	}

	// lifecycle hook functions
	useEffect(() => {
		if (props.type === 'new') dispatch(Actions.newCluster());
	}, [dispatch, props]);

	useEffect(() => {
		if (form?.data.network.region_id) {
			let costNodesTotal = 0;
			let costNode = 0;
			const sps = providers.filter(p => p.id === "maas");

			form.data.node.group.map(val => {
				const node = sps[0].instanceSizes.filter(r => r.instance_id === val.instance_id);
				const costNodes = node[0]?.cost?.cost_region[form.data.network.region_id];
				if (costNodes) {
					costNode = costNodes * val.count;
					costNodesTotal = costNodesTotal + costNode;
				}
				return 0;
			});
		}
	}, [form, providers]);

	useEffect(() => {
		const err = error || props.error;
		if (err && err.type === 'form') {
			const name = { name: err.message };
			formRef.current &&
				formRef.current.updateInputsWithError({
					...name
				});
			disableButton();
		}
	}, [error, props.error]);

	useEffect(() => {
		if ((cluster && !form) || (cluster && form && cluster.id !== form.id)) {
			setForm(cluster);
		}
	}, [form, cluster, setForm]);

	useEffect(() => {
		if (form && form.data.network.region_id) {
			providers.map(
				p =>
					p.id === 'maas' &&
					p.regions.map(r => r.zones && r.region_id === form.data.network.region_id && setZones(r.zones))
			);
		}
	}, [providers, form]);

	//Set instance sizes and node sizes here
	useEffect(() => {
		if (
			providers &&
			form &&
			(!nodeInstanceSizes || !serverInstanceSizes || !filteredCredentials || !regions || !zones)
		) {
			const P = providers.find(provider => provider.id === "maas");
			const InstSizes = P.instanceSizes;
			const Regions = P.regions;
			if (props.type !== 'new') {
				if (InstSizes.filter(s => s.instance_id === form.data.server.instance_id).length === 0) {
					InstSizes.push({ instance_id: form.data.server.instance_id, name: form.data.server.instance_id, cores: 0 });
				}
				if (Regions.filter(r => r.region_id === form.data.network.region_id).length === 0) {
					Regions.push({
						region_id: form.data.network.region_id,
						name: form.data.network.region_id,
						zones: [form.data.network.zone_id]
					});
				}

				for (const gs of form.data.node.group) {
					let contains = false;
					for (const s of InstSizes) {
						if (s.instance_id === gs.instance_id) {
							contains = true;
						}
					}

					if (!contains) {
						InstSizes.push({ instance_id: gs.instance_id, name: gs.instance_id, cores: 0 });
					}
				}
			}
			const C = credentials.filter(cred => cred.provider === "maas");
			setNodeInstanceSizes(InstSizes);
			setServerInstanceSizes(InstSizes);
			setFilteredCredentials(C);
			setRegions(Regions);
			if (form.data.network.region_id) {
				Regions.map(r => r.zones && r.region_id === form.data.network.region_id && setZones(r.zones));
			}
		}
	}, [
		providers,
		props,
		form,
		credentials,
		nodeInstanceSizes,
		serverInstanceSizes,
		filteredCredentials,
		regions,
		zones
	]);


	//prefix:
	useEffect(() => {
		setInputValue(form?.info?.prefix);
	}, [form]);

	useEffect(() => {
		const initialValues = form?.data?.node?.group.map(group => group.group_node_prefix || '');
		setGroupNodeInputValues(initialValues);
	}, [form]);

	useEffect(() => {
		if (form && form.data.server.disk_type !== form.data.node.disk_type) {
			setNodeObj({ name: 'disk_type', value: form.data.server.disk_type });
		}
	}, [form]);

	useEffect(() => {
		dispatch(Actions.provider_os_types("maas"))
	}, [dispatch]);

	//node volumen prop
	useEffect(() => {
		if (form?.data.network.region_id) {
			const sps = providers.filter(p => p.id === "maas");
			const region = sps[0].regions.filter(r => r.region_id === form.data.network.region_id);
			const zone = region[0]?.zones?.filter(
				r => r.zone_id === form.data.network.availability_domain || r.zone_id === form.data.network.zone_id
			);

			const clustersInUse = clustersArr.filter(c => c.info.state !== 'down' && c.provider === "maas");
			let intances = clustersInUse.length;
			let network = clustersInUse.length;
			let cpus = 0;

			clustersInUse.forEach(c => {
				const fp = providers.filter(p => p.id === c.provider);
				//nodes used
				c.data.node.group.forEach(i => {
					intances = parseInt(intances) + parseInt(i.count);
					const ccores = fp[0].instanceSizes.filter(co => co.instance_id === i.instance_id);
					cpus = cpus + parseInt(ccores[0]?.cores) * i.count;
				});
				//server used
				const scores = fp[0].instanceSizes.filter(co => co.instance_id === c.data.server.instance_id);
				cpus = cpus + parseInt(scores[0]?.cores);
			});

			//server edict
			const ecores = sps[0].instanceSizes.filter(co => co.instance_id === form.data.server.instance_id);
			network++;
			intances++;
			// Guard against missing instance size info for preconfigured devices
			const serverCores = ecores[0]?.cores || 0;
			cpus = cpus + parseInt(serverCores);

			form.data.node.group.forEach(i => {
				intances = parseInt(intances) + parseInt(i.count);
				const eccores = sps[0].instanceSizes.filter(co => co.instance_id === i.instance_id);
				if (eccores[0]?.cores) cpus = cpus + parseInt(eccores[0].cores) * i.count;
			});

			setUsedCpus(cpus);
			setUsedIntances(intances);
			setUsedNetwork(network);

			zone?.[0]?.quotas?.cpus && setLimitIntances(zone[0]?.quotas?.cpus);
			zone?.[0]?.quotas?.intance && setLimitCpus(zone[0]?.quotas?.intance);
			zone?.[0]?.quotas?.network && setLimitNetwork(zone[0]?.quotas?.network);
		}
	}, [form, providers, clustersArr]);

	const handleClose = useCallback(() => {
		dispatch(ActionsDialog.closeDialog());
	}, [dispatch]);

	useEffect(() => {
		const handleKeyDown = (event) => {
			if (event.key === 'Escape') {
				handleClose();
			}
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [handleClose]);

	const disableButton = () => setIsFormValid(false);

	const enableButton = () => setIsFormValid(true);

	const handleDeletePackages = pkg => {
		const filteredPkgs = form.data.packages.filter(p => p !== pkg);
		setDataObj({ name: 'packages', value: filteredPkgs });
		const isInPkgs = form.data.packages.some(p => p === pkg);
		const isInDeletedPkgs = deletedPkgs.some(dp => dp === pkg);
		if (isInPkgs && !isInDeletedPkgs) {
			const delArr = form.data.packages.filter(p => p === pkg);
			setDeletedPkgs([...deletedPkgs, ...delArr]);
		}
	};

	const setDataObj = ({ name, value }) => {
		setForm(_.set({ ...form }, 'data', { ...form.data, [name]: value }));
	};

	const setNetworkObj = ({ name, value }) => {
		setForm(_.set({ ...form }, 'data', { ...form.data, network: { ...form.data.network, [name]: value } }));
	};

	const setNodeObj = ({ name, value }) => {
		setForm(_.set({ ...form }, 'data', { ...form.data, node: { ...form.data.node, [name]: value } }));
	};

	const combineSameConfigurationNodes = groups => {
		const seenGroups = {};
		groups.forEach(group => {
			const compositeId = `${group.group_node_prefix_label}-${group.group_node_prefix}-${group.instance_id}-${group.ocpu}-${group.mem}-${group.efa}`;
			if (compositeId in seenGroups)
				seenGroups[compositeId].count = parseInt(seenGroups[compositeId].count) + parseInt(group.count);
			else seenGroups[compositeId] = group;
		});
		const filteredGroups = Object.keys(seenGroups).map(key => seenGroups[key]);
		return filteredGroups;
	};

	const setNodePrefix = (i = 0, value) => {
		const groups = form.data.node.group;
		groups[i]['group_node_prefix'] = value;
		const groupFiltered = combineSameConfigurationNodes(groups);
		setForm(_.set({ ...form }, 'data', { ...form.data, node: { ...form.data.node, group: [...groupFiltered] } }));
	};
	const setNodePrefixLabel = (i = 0, value) => {
		const groups = form.data.node.group;
		groups[i]['group_node_prefix_label'] = value;
		const groupFiltered = combineSameConfigurationNodes(groups);
		setForm(_.set({ ...form }, 'data', { ...form.data, node: { ...form.data.node, group: [...groupFiltered] } }));
	}

	const setServerObj = ({ name, value }) => {
		if (name === 'instance_id' && !value.includes('Flex')) {
			form.data.server.ocpus = form.data.server.mem = 1;
			setForm(_.set({ ...form }, 'data', { ...form.data, server: { ...form.data.server, [name]: value } }));
		} else {
			setForm(_.set({ ...form }, 'data', { ...form.data, server: { ...form.data.server, [name]: value } }));
		}
	};

	// Load specific preconfigured device configuration
	// familyOverride allows passing the family directly when calling from onChange before state updates
	const loadPreconfiguredDeviceById = (deviceId, familyOverride) => {
		const family = familyOverride || selectedDeviceFamily;
		
		if (!deviceId || !family) {
			console.warn('No device ID or device family selected');
			return;
		}
		
		const device = family.devices.find(d => d.id === deviceId);
		if (!device) {
			console.error('Preconfigured device not found:', deviceId);
			return;
		}

		setSelectedPreconfiguredDevice(device);
		
		// Clear compute nodes when selecting a new head node
		// (can't include the head node as a compute node)
		setSelectedCommissionedComputeNodes(prev => prev.filter(id => id !== deviceId));

		// Pre-populate form with device data
		setForm({
			...form,
			credential_id: null, // Preconfigured devices don't use credentials
			deviceType: 'commissioned', // Mark as commissioned cluster
			commissionedMachineId: device.id, // Reference to the commissioned machine
			data: {
				...form.data,
				manager: 'none', // Commissioned machines only support 'none' manager
				server: {
					...form.data.server,
					machine: family.machine_type,
					instance_id: device.instance_id,
					image_name: family.machine_type,
					volume_size: 100,
					isPrivate: false
				},
				network: {
					...form.data.network,
					region_id: device.region_id,
					zone_id: device.zone_id,
					public_ip: device.public_ip,
					private_static_ip: device.private_ip
				},
				ssh_username: device.ssh_username,
				keys: {
					...form.data.keys,
					public: device.ssh_public_key,
					private: 'preconfigured-device-no-private-key-needed'
				},
				// No standard compute nodes for commissioned devices (uses commissionedComputeNodes instead)
				node: {
					...form.data.node,
					group: []
				}
			},
			info: {
				...form.info,
				'os-type': family.os_type,
				terraform: {
					publicIp: device.public_ip,
					privateIp: device.private_ip,
					username: device.ssh_username
				}
			}
		});
	};

	const setInfoObj = ({ name, value }) => {
		setForm(_.set({ ...form }, 'info', { ...form.info, [name]: value }));
	};

	const setPackagesArr = () => {
		const pkg = pkgRef.current.value;
		setForm(_.set({ ...form }, 'data', { ...form.data, packages: [...form.data.packages, pkg] }));
		pkgRef.current.value = null;
	};

	const handleUpload = e => {
		const file = e.target.files[0];
		if (!file) return;

		//reset value to '' so that the same file can be uploaded if removed
		e.target.value = '';

		const reader = new FileReader();
		reader.readAsBinaryString(file);
		reader.onload = () => {
			setForm(
				_.set({ ...form }, 'data', { ...form.data, provision: { ...form.data.provision, script: reader.result } })
			);
		};
	};

	const areNonFormsyRequiredFieldsValid = () => {
		const serverPrefixisValid = !prefixError && form.data.server.image_name
		const nodePrefixesAreValid = nodePrefixErrors.every(error => error === '') && form.data.node.group.every(node => node.group_node_prefix)
		const headnodeInstanceSizeIsValid = form.data.server.instance_id
		const nodeInstanceSizesAreValid = form.data.node.group.every(node => node.instance_id)
		// When device family is not 'standard', a specific device MUST be selected from the dropdown
		// This prevents saving commissioned clusters without an actual commissioned machine reference
		const isCommissionedDeviceType = deviceType !== 'standard'
		const deviceSelectionValid = !isCommissionedDeviceType || (selectedPreconfiguredDevice !== null && selectedPreconfiguredDevice.id)
		return serverPrefixisValid && nodePrefixesAreValid && headnodeInstanceSizeIsValid && nodeInstanceSizesAreValid && deviceSelectionValid
	}

	// Validation functions
	const validationInput = (type, instanceid, currentvalue, ocpus, provider) => {
		return validationInputUlt(type, instanceid, currentvalue, ocpus, provider);
	};

	const validationMess = (type, instanceid, ocpus, provider) => {
		return validationMessUlt(type, instanceid, ocpus, provider);
	};

	const handleDeleteScript = () => {
		setForm(_.set({ ...form }, 'data', { ...form.data, provision: { ...form.data.provision, script: '' } }));
	};

	const handleHeteroCountChange = (dir, i) => {
		let totalCount = 1;
		if (dir === 'add' && (totalCount !== 250 || form.data.node.group.length < 250)) {
			setForm(
				_.set({ ...form }, 'data', {
					...form.data,
					node: { ...form.data.node, group: [...form.data.node.group, { count: '0', instance_id: '' }] }
				})
			);
		}
		if (dir === 'sub' && (totalCount > 1 || form.data.node.group.length > 1)) {
			const group = form.data.node.group;
			group.splice(i, 1);
			setForm(_.set({ ...form }, 'data', { ...form.data, node: { ...form.data.node, group } }));
		}
	};

	const startSave = () => handleSave({ confirmed: false });
	const startEdit = () => handleEdit({ confirmed: false });

	const handleEdit = ({ confirmed }) => {
		if (confirmed) {
			dispatch(ActionsDialog.closeDialog());
		}
		if (error) {
			dispatch(Actions.clearError());
		}
		if (form?.info?.lastDeployTime) form.info.lastDeployTime = 0;
		if (form?.info?.lastDestroyTime) form.info.lastDestroyTime = 0;
		form.data.node.group = form.data.node.group;
		form.data.node.group.forEach(node => {
			node.efa = false;
		});

		// Set imageMode in info object (matches schema)
		form.info.imageMode = imageMode;
		
		// Include excluded global scripts at root level (Feature 002 enhancement)
		form.excludedGlobalScripts = excludedGlobalScripts;
		
		// Include cluster applications (Feature: cluster-apps)
		form.applications = clusterApplications || [];
		
		// Update deviceType, commissionedMachineId, commissionedComputeNodes, and location based on current selection
		const updatedForm = {
			...form,
			deviceType: selectedPreconfiguredDevice ? 'commissioned' : 'standard',
			commissionedMachineId: selectedPreconfiguredDevice ? selectedPreconfiguredDevice.id : null,
			// Include commissioned compute nodes for commissioned devices
			commissionedComputeNodes: selectedPreconfiguredDevice ? selectedCommissionedComputeNodes : [],
			// Geographic location for WorldMap visualization
			location: regionToLocation(selectedRegion)
		};
		
		// Add owner selection if super-admin selected a different owner
		const editData = { cluster: updatedForm };
		if (currentUser.role && currentUser.role.includes('super-admin') && selectedOwner) {
			editData.ownerUsername = selectedOwner;
		}
		
		dispatch(Actions.editCluster(editData.cluster, deletedPkgs, editData.ownerUsername));
	};
	const handleSave = ({ confirmed }) => {
		if (props.setOldForm) {
			props.setOldForm(form);
		}
		if (confirmed) {
			dispatch(ActionsDialog.closeDialog());
		}

		form.data.node.group.forEach(node => {
			node.efa = false
		});
		form.info.bursting.mode = "off"
		form.data.node.group = form.data.node.group
		const updatedForm = {
			...form,
			excludedGlobalScripts,
			applications: clusterApplications || [],
			data: {

				...form.data,
			},
			info: {
				...form.info,
				prefix: inputValue,
				"os-type": form.info['os-type'],
				vncTokenAuthentication: isVncTokenAuthentication,
				vncRemoteClient: isVncRemoteClient,
				imageMode: imageMode
			},
			provider: 'maas',
			// Include deviceType, commissionedMachineId, and commissionedComputeNodes for commissioned devices
			// Use selectedPreconfiguredDevice to determine if commissioned (not deviceType state)
			deviceType: selectedPreconfiguredDevice ? 'commissioned' : 'standard',
			commissionedMachineId: selectedPreconfiguredDevice ? selectedPreconfiguredDevice.id : null,
			// Include commissioned compute nodes for commissioned devices
			commissionedComputeNodes: selectedPreconfiguredDevice ? selectedCommissionedComputeNodes : [],
			// Geographic location for WorldMap visualization
			location: regionToLocation(selectedRegion)
		}

		// Add owner selection if super-admin selected a different owner
		const saveData = { ...updatedForm };
		if (currentUser.role && currentUser.role.includes('super-admin') && selectedOwner) {
			saveData.ownerUsername = selectedOwner;
		}

		dispatch(Actions.saveCluster(saveData));
	};

	const [burstMode, setBurstMode] = useState(null)

	useEffect(() => {
		if (burstMode !== null) return
		if (props.type === 'new') {
			setBurstMode('max')
			return
		}
		if (form?.info?.bursting?.mode) setBurstMode(form.info.bursting.mode)
	}, [form, props.type, burstMode]);

	useEffect(() => {
		if (burstMode === null) return
		if (!form?.info) return
		form.info.bursting.mode = burstMode
	}, [form, burstMode]);

	const validateServerPrefix = (prefix) => { // Since "Node Prefix" field is not a "Formsy" input. This is how we need to show validations.
		const invalidPrefixCharacter = 'Invalid characters in the prefix. Use only lowercase letters, numbers, and hyphens.';
		const invalidPrefixHyphenUsage = 'Hyphens cannot start or end the prefix, and consecutive hyphens are not allowed.';
		if (/[^a-z0-9-]/g.test(prefix)) setPrefixError(invalidPrefixCharacter);
		else if (/^-|-$|--/g.test(prefix)) setPrefixError(invalidPrefixHyphenUsage);
		else setPrefixError('');
	}

	const validateNodePrefix = (prefix, index) => {
		const invalidPrefixCharacter = 'Invalid characters in the prefix. Use only lowercase letters, numbers, and hyphens.';
		const invalidPrefixHyphenUsage = 'Hyphens cannot start or end the prefix, and consecutive hyphens are not allowed.';
		let error = '';
		if (/[^a-z0-9-]/g.test(prefix)) error = invalidPrefixCharacter;
		else if (/^-|-$|--/g.test(prefix)) error = invalidPrefixHyphenUsage;

		setNodePrefixErrors(prevErrors => {
			const newErrors = [...prevErrors];
			newErrors[index] = error;
			return newErrors;
		});
	};

	// Card styling - matching AWS form pattern
	const cardStyle = {
		marginBottom: '24px',
		boxShadow: '0 2px 10px 0 rgba(0,0,0,0.1)',
		borderRadius: '8px',
		overflow: 'visible'
	};

	const cardHeaderStyle = {
		paddingBottom: '8px',
		backgroundColor: '#f7f9fc'
	};

	const sectionTitleStyle = {
		fontSize: '16px',
		fontWeight: 600,
		color: '#0f2147'
	};

	useEffect(() => {
		if (!form) return
		setForm(form)
	}, [form]);

	return (
		<React.Fragment>
			{form && (
				<React.Fragment>
					<DialogTitle className={clsx(classes.modalHeader, 'h-54 min-h-54 p-20')}>
						<div className="flex flex-1 w-full items-center justify-between">
							<div className="flex flex-col max-w-full">
								<div className="flex items-center max-w-full">
									<FuseAnimate animation="transition.expandIn" delay={300}>
										<img
											id="provider-logo"
											className="w-32 sm:w-48 mr-8 sm:mr-16 rounded"
											src="assets/images/providers/acm.png"
											alt="Provider Logo"
										/>
									</FuseAnimate>
									<div className="flex flex-col min-w-0">
										<FuseAnimate animation="transition.slideLeftIn" delay={300}>
											<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
												<Typography id="cluster-name-header" className="text-16 text-white sm:text-20 truncate">
													{form.name || 'New Cluster'}
												</Typography>
												{props.type === 'edit' && form?.id && (
													<Typography style={{color: '#ccc', fontSize: '12px', fontWeight: 'normal', marginLeft: '16px'}}>
														ID: {form.id}
													</Typography>
												)}
											</div>
										</FuseAnimate>
										<FuseAnimate animation="transition.slideLeftIn" delay={300}>
											<Typography id="provider-name-header" className="text-white" variant="caption">
												{"ACM"}
											</Typography>
										</FuseAnimate>
									</div>
								</div>
							</div>
							<div>
								<Button
									className="whitespace-no-wrap mr-2"
									id="advanced-btn"
									variant="contained"
									disabled={loading}
									onClick={() => setAdvanced(!advanced)}
								>
									{advanced ? "Basic" : "Advanced"}
								</Button>
								{props.type === 'new' && (
									<Button
										className="whitespace-no-wrap mx-2"
										id="save-btn"
										disabled={
											!isFormValid ||
											!areNonFormsyRequiredFieldsValid()
										}
										variant="contained"
										onClick={() => {
											startSave();
										}}
									>
										{'Save'}
									</Button>
								)}
								{props.type === 'edit' && form.info.state === CLUSTER_STATES.DESTROYED && (
									<Button
										className="whitespace-no-wrap mx-2"
										id="save-btn"
										disabled={!isFormValid || loading || !form.data.server.image_name || !areNonFormsyRequiredFieldsValid()}
										variant="contained"
										onClick={() => {
											startEdit()
										}}
									>
										update
									</Button>
								)}
								<Button
									className="whitespace-no-wrap ml-2"
									id="close-btn"
									variant="contained"
									onClick={handleClose}
								>
									Close
								</Button>
							</div>
						</div>
					</DialogTitle>
					{usedCpus > limitCpus && (
						<DialogContent className={clsx(classes.content, 'flex flex-col items-center max-w-full p-16 ')}>
							<span className={classes.error2}>
								<Icon>error</Icon> <span>The quota of CPUs established by the provider is exceeded</span>
							</span>
						</DialogContent>
					)}
					{usedNetwork > limitNetwork && (
						<DialogContent className={clsx(classes.content, 'flex flex-col items-center max-w-full p-16 ')}>
							<span className={classes.error2}>
								<Icon>error</Icon> <span>The quota of network established by the provider is exceeded</span>
							</span>
						</DialogContent>
					)}
					{usedIntances > limitIntances && (
						<DialogContent className={clsx(classes.content, 'flex flex-col items-center max-w-full p-16 ')}>
							<span className={classes.error2}>
								<Icon>error</Icon> <span>The quota of Instances established by the provider is exceeded</span>
							</span>
						</DialogContent>
					)}

					<DialogContent className={clsx(classes.content, 'flex flex-col justify-start items-center max-w-full p-16')}>
					<DisabledFormWrapper isDisabled={disableInputs}>
						<Formsy  ref={formRef} onValid={enableButton} onInvalid={disableButton}>
							{/* Owner Selection Card - Only for Super Admin in Advanced Mode */}
							<OwnerSelectionCard
								value={selectedOwner}
								onChange={setSelectedOwner}
								label="Cluster Owner"
								title="Cluster Owner"
								showInAdvancedMode={true}
								advanced={advanced}
								disabled={props.type === 'edit' && form && form.info && form.info.state !== CLUSTER_STATES.DESTROYED}
							/>
							
							{/* General Section */}
							<Card style={cardStyle}>
								<CardHeader
									title={
										<Box display="flex" alignItems="center">
											<Icon style={{marginRight: '8px', color: '#2196F3'}}>info</Icon>
											<Typography style={sectionTitleStyle}>General</Typography>
										</Box>
									}
									style={cardHeaderStyle}
								/>
								<CardContent>
									<div className="flex flex-row mb-16 w-full">
										<TextFieldFormsy
											className={clsx(classes.input)}
											id="name"
											name="name"
											label="Name"
											type="text"
											variant="outlined"
											value={form.name}
											onChange={handleChange}
											fullWidth
											required
											placeholder="Your Cluster Name (No Spaces or Capitals)"
											validations={{
												isNameUnique: (formValues, fieldValue) => {
													if (clusterNames.includes(fieldValue) && !(props.type === 'edit')) {
														return 'This name is already in use. Please choose a different one.';
													}
													return true;
												},
												isString: (formValues, fieldValue) => typeof fieldValue === 'string' || 'Name must be a string.',
												doesNotExceedMaxLength: (formValues, fieldValue) =>
													!fieldValue || fieldValue.length <= 15 || 'Name cannot exceed 15 characters.',
												hasValidCharacters: (formValues, fieldValue) =>
													!fieldValue || !/[^a-z0-9-]/g.test(fieldValue) || 'Invalid characters in the name. Use only lowercase letters, numbers, and hyphens.',
												doesNotStartOrEndWithHyphen: (formValues, fieldValue) =>
													!fieldValue || !/^-|-$|--/g.test(fieldValue) || 'Hyphens cannot start or end the name, and consecutive hyphens are not allowed.',
												doesNotStartWithInteger: (formValues, fieldValue) =>
													!fieldValue || !/^\d/.test(fieldValue) || 'Name cannot start with a number.',
												}}
											validationErrors={{
												isNameUnique: 'This name is already in use. Please choose a different one.',
												isString: 'Name must be a string.',
												doesNotExceedMaxLength: 'Name cannot exceed 15 characters.',
												hasValidCharacters: 'Invalid characters in the name. Use only lowercase letters, numbers, and hyphens.',
												doesNotStartOrEndWithHyphen: 'Hyphens cannot start or end the name, and consecutive hyphens are not allowed.',
												doesNotStartWithInteger: 'Name cannot start with a number.'
											}}
										/>
									</div>
									<div className="flex flex-row mb-16 w-full">
										<SelectFormsy
											className={clsx(classes.input, 'w-1/3 mr-8')}
											id="device-type"
											name="device-type"
											label="Device Family *"
											type="select"
											variant="outlined"
											value={deviceType}
											onChange={(e) => {
												const newDeviceType = e.target.value;
												setDeviceType(newDeviceType);
												
												// Clear commissioned compute nodes when changing device family
												setSelectedCommissionedComputeNodes([]);
												
												if (newDeviceType !== 'standard') {
													const family = preconfiguredDevices.find(df => df.machine_type === newDeviceType);
													setSelectedDeviceFamily(family);
													setServerObj({ name: 'machine', value: newDeviceType });
													// Auto-select first device in the family - pass family directly since state hasn't updated yet
													if (family && family.devices && family.devices.length > 0) {
														loadPreconfiguredDeviceById(family.devices[0].id, family);
													} else {
														setSelectedPreconfiguredDevice(null);
													}
												} else {
													setSelectedDeviceFamily(null);
													setSelectedPreconfiguredDevice(null);
													setServerObj({ name: 'machine', value: '' });
												}
											}}
											fullWidth
											required
											MenuProps={{
												PaperProps: {
													style: { maxHeight: 300 }
												}
											}}
										>
											{/* Always show Standard option */}
											<MenuItem value="standard">Standard (MaaS Deploy)</MenuItem>
											{preconfiguredDevices.map(family => (
												<MenuItem key={family.machine_type} value={family.machine_type}>
													{family.name} (Commissioned)
												</MenuItem>
											))}
										</SelectFormsy>

										{/* Only show OS Type selector for standard deployments */}
										{deviceType === 'standard' && (
											<SelectFormsy
												className={clsx(classes.input, 'w-1/3 mr-8')}
												id="os-type"
												name="os-type"
												label="OS Type"
												variant="outlined"
												value={form.info['os-type'] || ''}
												onChange={(e) => setInfoObj({ name: 'os-type', value: e.target.value })}
												fullWidth
												required
												MenuProps={{
													PaperProps: {
														style: { maxHeight: 300 }
													}
												}}
											>
												{supportedOSs.map((os, index) => (
													<MenuItem key={index} value={os}>
														{os}
													</MenuItem>
												))}
											</SelectFormsy>
										)}

										{/* Show credential selector for standard devices, device selector for preconfigured devices */}
										{deviceType === 'standard' ? (
											<SelectFormsy
												className={clsx(classes.input, 'w-1/3')}
												id="credential_id"
												name="credential_id"
												label="Credential *"
												type="select"
												variant="outlined"
												value={form.credential_id}
												onChange={handleChange}
												fullWidth
												required
												MenuProps={{
													PaperProps: {
														style: { maxHeight: 300 }
													}
												}}
											>
												{filteredCredentials.length > 0 ? (
													filteredCredentials.map((c, i) => (
														<MenuItem id={c.name} key={i} value={c.id}>
															{c.name}
														</MenuItem>
													))
												) : (
													<MenuItem disabled value="">
														Please add credentials first
													</MenuItem>
												)}
											</SelectFormsy>
										) : (
											<SelectFormsy
												className={clsx(classes.input, 'w-1/3')}
												id="preconfigured-device-id"
												name="preconfigured-device-id"
												label="Device *"
												type="select"
												variant="outlined"
												value={selectedPreconfiguredDevice?.id || ''}
												onChange={(e) => {
													const deviceId = e.target.value;
													if (deviceId) {
														loadPreconfiguredDeviceById(deviceId);
													}
												}}
												fullWidth
												required
												InputLabelProps={{ shrink: true }}
												MenuProps={{
													PaperProps: {
														style: { maxHeight: 300 }
													}
												}}
											>
												{selectedDeviceFamily && selectedDeviceFamily.devices.length > 0 ? (
													selectedDeviceFamily.devices.map((device, i) => (
														<MenuItem key={i} value={device.id}>
															{device.name}
														</MenuItem>
													))
												) : (
													<MenuItem disabled value="">No devices available</MenuItem>
												)}
											</SelectFormsy>
										)}
									</div>
									<div className="flex flex-row mb-16 w-full">
										<SelectFormsy
											className={clsx(classes.input, 'w-full')}
											id="manager"
											name="manager"
											label="Manager *"
											type="select"
											variant="outlined"
											value={form.data.manager}
											onChange={e => setDataObj(e.target)}
											fullWidth
											required
											MenuProps={{
												PaperProps: {
													style: { maxHeight: 200 }
												}
											}}
										>
											<MenuItem id="maas_none" value="none">
												None
											</MenuItem>
											{/* Only show Slurm and Torque for standard MaaS clusters, not for commissioned machines */}
											{deviceType === 'standard' && (
												<MenuItem id="maas_slurm" value="slurm">
													Slurm
												</MenuItem>
											)}
											{deviceType === 'standard' && (
												<MenuItem id="torque" value="torque">
													Torque
												</MenuItem>
											)}
										</SelectFormsy>
									</div>
									<TextField
										className={clsx(classes.input, 'w-full')}
										id="description"
										name="description"
										label="Description"
										type="textarea"
										variant="outlined"
										value={form.description}
										onChange={handleChange}
										fullWidth
										multiline
										minRows={3}
									/>
									<div className="flex flex-row mt-16 w-full">
										<Autocomplete
											className={clsx(classes.input, 'w-full')}
											id="region-selector"
											options={getRegionsGroupedByContinent()}
											groupBy={(option) => option.continent}
											getOptionLabel={(option) => getRegionLabel(option)}
											value={selectedRegion}
											onChange={(event, newValue) => {
												setSelectedRegion(newValue);
											}}
											renderInput={(params) => (
												<TextField
													{...params}
													label="Geographic Location"
													variant="outlined"
													placeholder="Select region for map positioning"
													helperText="Location where the cluster is physically deployed (for dashboard map)"
												/>
											)}
											renderOption={(option) => (
												<Box display="flex" flexDirection="column">
													<Typography variant="body1">{option.city}, {option.country}</Typography>
												</Box>
											)}
										/>
									</div>
								</CardContent>
							</Card>

							{/* Resource Manager Configuration */}
							{form.data.manager === RESOURCE_MANAGER.TORQUE && (
								<Card style={cardStyle}>
									<CardHeader
										title={
											<Box display="flex" alignItems="center">
												<Icon style={{marginRight: '8px', color: '#2196F3'}}>settings_applications</Icon>
												<Typography style={sectionTitleStyle}>Resource Manager Configuration</Typography>
											</Box>
										}
										style={cardHeaderStyle}
									/>
									<CardContent>
										<div className="flex flex-row w-full gap-16">
											<TextFieldFormsy
												className={clsx(classes.input, 'flex-1')}
												id="hostname"
												name="hostname"
												label="Hostname"
												type="text"
												variant="outlined"
												value={form.data.moab.hostname}
												fullWidth
												required={form.data.manager === RESOURCE_MANAGER.TORQUE}
												placeholder="Host Name"
												onChange={e => setClusterInfo(e)}
											/>
											<TextFieldFormsy
												className={clsx(classes.input, 'flex-1')}
												id="ip"
												name="ip"
												label="IP Address"
												type="text"
												variant="outlined"
												value={form.data.moab.ip}
												fullWidth
												required={form.data.manager === RESOURCE_MANAGER.TORQUE}
												placeholder="IP Address"
												onChange={e => setClusterInfo(e)}
												validations={{
													ipValid: (formValues, fieldValue) => {
														return ipValid(fieldValue);
													}
												}}
												validationErrors={{
													ipValid: 'Not a valid IP'
												}}
											/>
										</div>
									</CardContent>
								</Card>
							)}

							{/* RHEL Configuration */}
							{form.info["os-type"].startsWith('rhel') && (
								<Card style={cardStyle}>
									<CardHeader
										title={
											<Box display="flex" alignItems="center">
												<Icon style={{marginRight: '8px', color: '#2196F3'}}>verified_user</Icon>
												<Typography style={sectionTitleStyle}>RHEL Configuration</Typography>
											</Box>
										}
										style={cardHeaderStyle}
									/>
									<CardContent>
										<div className="flex flex-row w-full gap-16">
											<TextFieldFormsy
												className={clsx(classes.input, 'flex-1')}
												id="rhel_activation_key"
												name="rhel_activation_key"
												label="Activation Key"
												type="text"
												variant="outlined"
												value={form.data?.rhel_activation_key || ''}
												onChange={e => {
													form.data = {
														...form.data,
														rhel_activation_key: e.target.value,
													};
													setForm({ ...form });
												}}
												fullWidth
												placeholder="Activation Key"
											/>
											<TextFieldFormsy
												className={clsx(classes.input, 'flex-1')}
												id="rhel_organization_id"
												name="rhel_organization_id"
												label="Organization ID"
												type="text"
												variant="outlined"
												value={form.data?.rhel_organization_id || ''}
												onChange={e => {
													form.data = {
														...form.data,
														rhel_organization_id: e.target.value,
													};
													setForm({ ...form });
												}}
												fullWidth
												placeholder="Organization ID"
											/>
										</div>
									</CardContent>
								</Card>
							)}
							{/* VNC Settings - only in Advanced mode */}
							{advanced && (
								<Card style={cardStyle}>
									<CardHeader
										title={
											<Box display="flex" alignItems="center">
												<Icon style={{marginRight: '8px', color: '#2196F3'}}>desktop_windows</Icon>
												<Typography style={sectionTitleStyle}>VNC Settings</Typography>
											</Box>
										}
										style={cardHeaderStyle}
									/>
									<CardContent>
										<div className="flex flex-row items-center justify-start">
											<div className="flex flex-row items-center justify-start w-1/2 mr-8">
												VNC Token Authentication:
												<Checkbox
													checked={isVncTokenAuthentication}
													onChange={event => {
														const newValue = event.target.checked; 
														setIsVncTokenAuthentication(newValue); 
														setForm(prevForm => ({
															...prevForm,
															info: {
																...prevForm.info,
																vncTokenAuthentication: newValue 
															}
														}));
													}}
													name="vncTokenAuthentication"
													color="primary"
												/>
											</div>
											<div className="flex flex-row items-center justify-start w-1/2 ml-8">
												VNC Remote Client:
												<Checkbox
													checked={isVncRemoteClient}
													onChange={event => {
														const newValue = event.target.checked;
														setIsVncRemoteClient(newValue);
														setForm(prevForm => ({
															...prevForm,
															info: {
																...prevForm.info,
																vncRemoteClient: newValue
															}
														}));
													}}
													name="vncRemoteClient"
													color="primary"
												/>
											</div>
										</div>
									</CardContent>
								</Card>
							)}

							{/* Head Node Section - only for standard deployments */}
							{deviceType === 'standard' && (
								<Card style={cardStyle}>
									<CardHeader
										title={
											<Box display="flex" alignItems="center">
												<Icon style={{marginRight: '8px', color: '#2196F3'}}>dns</Icon>
												<Typography style={sectionTitleStyle}>Head Node</Typography>
											</Box>
										}
										style={cardHeaderStyle}
									/>
									<CardContent>
										<div className="flex flex-row mb-16 w-full">
											<Autocomplete
												id="server-image_name"
												className={clsx(classes.input, 'flex-grow', 'mr-8')}
												options={headNodeImages}
												getOptionLabel={(option) => option.name}
												renderInput={(params) =>
													<TextField
														{...params}
														label="Head Node Image"
														placeholder="Your Prefix Name or Select Stack"
														required
														variant="outlined"
														fullWidth
														error={prefixError ? true : false}
														helperText={prefixError}
													/>
												}
												onChange={(event, newValue) => {
													form.data.server.image_name = newValue ? newValue.name : ''
													setForm({ ...form })
													validateServerPrefix(newValue ? newValue.name : '')
												}}
												value={form.data.server.image_name ? { name: form.data.server.image_name } : null}
											/>

											<Autocomplete
												id="server-instance_id"
												className="w-1/3"
												options={getAvailableMachines()}
												getOptionLabel={(option) => option.name}
												renderInput={(params) => <TextField {...params} label="Head Node Size *" variant="outlined" />}
												required
												onChange={handleHeadNodeShapeChange}
												value={
													form.data.server.instance_id
														? nodeShapes.find(option => option.instance_id === form.data.server.instance_id) || null
														: null
												}
											/>
										</div>
										{selectedHeadNodeShape && selectedHeadNodeShape.all_machines.length > 0 && (
											<div className="flex flex-col w-full">
												<label className="font-bold mb-4">Select Machine</label>
												<div className="flex space-x-6">
													{selectedHeadNodeShape.all_machines.map((machine) => (
														<div key={machine} className="flex items-center mr-8">
															<input
																type="radio"
																id={machine}
																name="machine"
																value={machine}
																checked={form.data.server.machine === machine}
																onChange={(event) => {
																	const selectedMachine = event.target.value;
																	setForm((prevForm) => {
																		const updatedForm = JSON.parse(JSON.stringify(prevForm));
																		updatedForm.data.server.machine = selectedMachine;
																		if (Array.isArray(updatedForm.data.node.group)) {
																			updatedForm.data.node.group = updatedForm.data.node.group.map((group) => {
																				if (!Array.isArray(group.machines)) {
																					group.machines = []; 
																				}
																				group.machines = group.machines.filter((machine) => machine !== selectedMachine);
																				group.count = group.machines.length;
																				return group;
																			});
																		}
																		return updatedForm;
																	});
																}}
																className="mr-2"
															/>
															<label htmlFor={machine}>{machine}</label>
														</div>
													))}
												</div>
											</div>
										)}
										{selectedHeadNodeShape && selectedHeadNodeShape.all_machines.length === 0 && (
											<div className="flex flex-col w-full">
												<label className="font-bold mb-4">No machines available for the selected shape</label>
											</div>
										)}
									</CardContent>
								</Card>
							)}
							
							{/* Compute Nodes Section - only for standard deployments */}
							{deviceType === 'standard' ? (
								<Card style={cardStyle}>
									<CardHeader
										title={
											<Box display="flex" alignItems="center" justifyContent="space-between">
												<Box display="flex" alignItems="center">
													<Icon style={{marginRight: '8px', color: '#2196F3'}}>memory</Icon>
													<Typography style={sectionTitleStyle}>Compute Nodes</Typography>
												</Box>
												<Fab
													id="add-node-size-btn"
													color="secondary"
													size="small"
													aria-label="add"
													onClick={() => handleHeteroCountChange('add')}
												>
													<Icon>add</Icon>
												</Fab>
											</Box>
										}
										style={cardHeaderStyle}
									/>
									<CardContent>
										{nodeInstanceSizes &&
											form.data.node.group.map((group, i) => (
									<div key={`node-${i}`}>
										<div className="flex flex-row w-full mb-16">
											<TextFieldFormsy
												className={clsx(classes.input, 'w-1/2 mr-8')}
												id="group_node_prefix_label"
												name="group_node_prefix_label"
												label="Label"
												type="text"
												variant="outlined"
												value={group.group_node_prefix_label || ''}
												onChange={e => setNodePrefixLabel(i, e.target.value)}
												fullWidth
												required
												placeholder="Label"
											/>
											<Autocomplete
												id={`group-node-image_name-${i}`}
												className={clsx(classes.input, 'w-1/2', 'mr-8')}
												options={computeNodeImges}
												getOptionLabel={(option) => option.name}
												renderInput={(params) => (
													<TextField
														{...params}
														label="Compute Node Image"
														required
														variant="outlined"
														fullWidth
														error={!!nodePrefixErrors[i]}
														helperText={nodePrefixErrors[i]}
													/>
												)}
												onChange={(event, newValue) => {
													const newPrefix = newValue ? newValue.name : '';
													setGroupNodeInputValues(prevValues => ({
														...prevValues,
														[i]: newPrefix
													}));
													setNodePrefix(i, newPrefix);
													validateNodePrefix(newPrefix, i);
												}}
												value={groupNodeInputValues[i] ? { name: groupNodeInputValues[i] } : null}
											/>
											<Autocomplete
												id="group-node-instance_id"
												className="w-1/3"
												options={getAvailableMachines()}
												getOptionLabel={(option) => option.name}
												getOptionSelected={(option, value) => option.instance_id === value.instance_id}
												renderInput={(params) => (
													<TextField {...params} label="Size *" variant="outlined" />
												)}
												onChange={(event, newValue) => handleNodeShapeChange(i, newValue)}
												value={nodeShapes.find(option => option.instance_id === group.instance_id) || null}
											/>
											<TextFieldFormsy
												className={clsx(classes.input, 'w-2/12 mx-8')}
												id={`count-${i}`}
												name="count"
												label="Count"
												type="number"
												variant="outlined"
												value={group?.machines?.length || '0'}
												onChange={e => {    
													const filteredMachines = getFilteredNodeMachines(group?.selectedNodeShape?.all_machines || [], i);
													let count = parseInt(e.target.value, 10);

													if (count > filteredMachines.length) {
														count = filteredMachines.length;
													}

													setForm(prevForm => {
														const updatedGroup = [...prevForm.data.node.group];
														updatedGroup[i].count = count;
														updatedGroup[i].machines = filteredMachines.slice(0, count);
														return {
															...prevForm,
															data: {
																...prevForm.data,
																node: {
																	...prevForm.data.node,
																	group: updatedGroup
																}
															}
														};
													});

													e.target.value = count;
												}}
												required
												fullWidth
												inputProps={{
													min: 0,
													max: group.selectedNodeShape ? getFilteredNodeMachines(group.selectedNodeShape.all_machines, i).length : 1,
												}}
											/>
											<div className="flex flex-col justify-center ml-8">
												<Fab
													id={`delete-btn-${i}`}
													disabled={form.data.node.group.length <= 1}
													color="secondary"
													size="small"
													aria-label="delete"
													onClick={() => handleHeteroCountChange('sub', i)}
												>
													<Icon className="item-center justify-center">delete</Icon>
												</Fab>
											</div>
										</div>
										{group.selectedNodeShape && group.selectedNodeShape.all_machines && (
											<div className="flex flex-col ml-8">
												{getFilteredNodeMachines(group.selectedNodeShape.all_machines, i).length > 0 ? (
												<>
													<label className="font-bold mb-4">Select Machines</label>
													<div className="flex space-x-4">
													{getFilteredNodeMachines(group.selectedNodeShape.all_machines, i).map((machine) => (
														<div key={machine} className="flex items-center mr-4">
														<input
															type="checkbox"
															id={machine}
															name="machine"
															value={machine}
															checked={group.machines?.includes(machine) || false}
															onChange={(event) => {
															const selectedMachines = group.machines || [];
															const newSelectedMachines = event.target.checked
																? [...selectedMachines, machine]
																: selectedMachines.filter(m => m !== machine);
															setForm(prevForm => {
																const updatedGroup = [...prevForm.data.node.group];
																updatedGroup[i].machines = newSelectedMachines;
																updatedGroup[i].count = newSelectedMachines.length;
																return {
																...prevForm,
																data: {
																	...prevForm.data,
																	node: {
																	...prevForm.data.node,
																	group: updatedGroup,
																	},
																},
																};
															});
															}}
															className="mr-2"
														/>
														<label>{machine}</label>
														</div>
													))}
													</div>
												</>
												) : (
												<div className="text-red-500">No available machines</div>
												)}
											</div>
										)}

										
									</div>
								))
							}
									</CardContent>
								</Card>
							) : (
								/* For preconfigured/commissioned devices, show compute node selection */
								<Card style={cardStyle}>
									<CardHeader
										title={
											<Box display="flex" alignItems="center">
												<Icon style={{marginRight: '8px', color: '#2196F3'}}>memory</Icon>
												<Typography style={sectionTitleStyle}>Commissioned Compute Nodes</Typography>
											</Box>
										}
										style={cardHeaderStyle}
									/>
									<CardContent>
										{/* Show available commissioned machines from same family that aren't the head node */}
										{selectedDeviceFamily && selectedDeviceFamily.devices && selectedDeviceFamily.devices.length > 1 ? (
											<div>
												<Typography variant="body2" style={{ marginBottom: '16px', color: '#666' }}>
													Select additional commissioned machines from the same device family to use as compute nodes.
													These machines will join the cluster when deployed.
												</Typography>
												<div className="flex flex-col">
													{selectedDeviceFamily.devices
														.filter(device => {
															// Exclude the head node (selectedPreconfiguredDevice)
															const deviceId = device.id || device._id;
															const headNodeId = selectedPreconfiguredDevice?.id || selectedPreconfiguredDevice?._id;
															return deviceId !== headNodeId;
														})
														.map((device) => {
															const deviceId = device.id || device._id;
															const isSelected = selectedCommissionedComputeNodes.includes(deviceId);
															return (
																<div key={deviceId} className="flex items-center mb-8 p-8" style={{ 
																	backgroundColor: isSelected ? '#e3f2fd' : '#f5f5f5',
																	borderRadius: '4px',
																	border: isSelected ? '1px solid #2196F3' : '1px solid #e0e0e0'
																}}>
																	<Checkbox
																		checked={isSelected}
																		onChange={(event) => {
																			if (event.target.checked) {
																				setSelectedCommissionedComputeNodes(prev => [...prev, deviceId]);
																			} else {
																				setSelectedCommissionedComputeNodes(prev => prev.filter(id => id !== deviceId));
																			}
																		}}
																		color="primary"
																		disabled={disableInputs}
																	/>
																	<div className="ml-8">
																		<Typography variant="body1" style={{ fontWeight: 500 }}>
																			{device.name}
																		</Typography>
																		<Typography variant="body2" style={{ color: '#666' }}>
																			{device.ipAddress || device.public_ip} • {device.architecture || 'auto'}
																		</Typography>
																	</div>
																</div>
															);
														})
													}
												</div>
												{selectedCommissionedComputeNodes.length > 0 && (
													<Typography variant="body2" style={{ marginTop: '12px', color: '#4CAF50' }}>
														{selectedCommissionedComputeNodes.length} compute node(s) selected
													</Typography>
												)}
											</div>
										) : (
											<Typography style={{ color: '#666' }}>
												{selectedDeviceFamily && selectedDeviceFamily.devices && selectedDeviceFamily.devices.length === 1 ? (
													'No additional commissioned machines available in this device family for compute nodes. Commission more machines of the same type to add compute nodes.'
												) : (
													'Select a device family and head node first to see available compute nodes.'
												)}
											</Typography>
										)}
									</CardContent>
								</Card>
							)}


							{/* Advanced Section - only in Advanced mode */}
							{advanced && (
								<Card style={cardStyle}>
									<CardHeader
										title={
											<Box display="flex" alignItems="center">
												<Icon style={{marginRight: '8px', color: '#2196F3'}}>settings</Icon>
												<Typography style={sectionTitleStyle}>Advanced Settings</Typography>
											</Box>
										}
										style={cardHeaderStyle}
									/>
									<CardContent>
										{/* Volume Settings */}
										<div className="flex flex-row mb-16 w-full">
											{/* Hide volume settings for commissioned devices */}
											{deviceType === 'standard' && (
												<>
													<TextFieldFormsy
														className={clsx(classes.input, 'w-1/2')}
														id="server-volume_size"
														name="volume_size"
														label="Server Volume (GB)"
														type="text"
														variant="outlined"
														value={form.data.server.volume_size}
														onChange={e => setServerObj(e.target)}
														fullWidth
														validations={{
															mustbenumber: (formValues, fieldValue) => {
																return validationInput(
																	'clusterservervolume',
																	formValues.instance_id,
																	fieldValue,
																	1,
																	"maas"
																);
															}
														}}
														validationErrors={{
															mustbenumber: validationMess('clusterservervolume', form.instance_id, 1, "maas")
														}}
													/>
													<TextFieldFormsy
														className={clsx(classes.input, 'ml-8 w-1/2')}
														id="node-volume_size"
														name="volume_size"
														label="Node Volume (GB)"
														type="text"
														variant="outlined"
														value={form.data.node.volume_size}
														onChange={e => setNodeObj(e.target)}
														fullWidth
														validations={{
															mustbenumber: (formValues, fieldValue) => {
																return validationInput('clusternodevolume', formValues.instance_id, fieldValue, 1, "maas");
															}
														}}
														validationErrors={{
															mustbenumber: validationMess('clusternodevolume', form.instance_id, 1, "maas")
														}}
													/>
												</>
											)}
											<TextFieldFormsy
												className={clsx(classes.input, deviceType === 'standard' ? 'ml-8 w-1/2' : 'w-full')}
												id="public_ip_id"
												name="public_ip"
												label="Public IP"
												type="text"
												variant="outlined"
												value={form.data.network.public_ip}
												onChange={e => setNetworkObj(e.target)}
												fullWidth
												placeholder="Example: 192.0.2.1"
											/>
										</div>

										{/* User Script and Share */}
										<div className="flex flex-row items-center justify-start mb-16">
											<div className="flex flex-row items-center justify-start w-2/3 mr-8">
												<div className="flex flex-row items-center w-1/2">
													<Typography className="font-bold mr-16">User Script:</Typography>
													<input
														className={classes.uploadInput}
														onChange={e => handleUpload(e)}
														id="upload-btn"
														type="file"
													/>
													<label htmlFor="upload-btn">
														<Button
															id="upload-label"
															className="whitespace-no-wrap mr-2"
															component="span"
															variant="contained"
															color="secondary"
														>
															Upload Script
														</Button>
													</label>
												</div>
												{!form.data.provision.script ? (
													<div className="flex items-center justify-start h-full">
														<Typography id="no-data-files-msg" color="textSecondary" variant="h6">
															No Script
														</Typography>
													</div>
												) : (
													<Paper
														elevation={0}
														variant="outlined"
														className={clsx(classes.border, 'flex items-center w-4/5 h-48 ml-4')}
													>
														<Typography id="script-name" className="text-center w-2/5">
															User Script
														</Typography>
														<Typography id="script-blank" className="w-2/5"></Typography>
														<Typography id="script-actions" className="flex items-center justify-center w-1/5 mr-2">
															<IconButton id="script-actions-delete" onClick={handleDeleteScript}>
																<Icon>delete</Icon>
															</IconButton>
															<IconButton id="script-actions-view" onClick={() => setScriptOpen(true)}>
																<Icon>visibility</Icon>
															</IconButton>
														</Typography>
													</Paper>
												)}
											</div>
											<div className="flex flex-row items-center justify-start w-1/3 ml-8">
												<SelectFormsy
													className={clsx(classes.input, 'w-full')}
													id="share"
													name="share"
													label="Share"
													type="select"
													variant="outlined"
													value={form.data.share}
													onChange={e => setDataObj(e.target)}
													fullWidth
													MenuProps={{
														PaperProps: {
															style: { maxHeight: 300 }
														}
													}}
												>
													<MenuItem id="nfs" value="nfs">
														NFS
													</MenuItem>
													<MenuItem id="beegfs" value="beegfs">
														BeeGFS
													</MenuItem>
												</SelectFormsy>
											</div>
										</div>

										{/* Global Provision Scripts Preview */}
										<Divider variant="fullWidth" className="my-16" />
										<GlobalProvisionScriptsPreview
											provider="maas"
											osType={form?.info?.['os-type']}
											clusterName={form?.name}
											clusterId={form?.id}
											excludedScripts={excludedGlobalScripts}
											onExcludedScriptsChange={setExcludedGlobalScripts}
										/>

										{/* Cluster Applications - Feature: cluster-apps */}
										<Divider variant="fullWidth" className="my-16" />
										<ClusterApplicationsSection
											applications={clusterApplications}
											onChange={setClusterApplications}
											disabled={disableInputs}
										/>

										{/* Packages */}
										<div className="flex flex-row items-center justify-start">
											<div className="flex flex-row items-center justify-start w-1/2 mr-8">
												<Typography className="font-bold mr-16">Packages:</Typography>
												<Paper
													elevation={0}
													variant="outlined"
													className={clsx(classes.borderHover, 'flex items-center h-48 p-4')}
												>
													<InputBase
														inputRef={pkgRef}
														className={clsx(classes.input, 'mx-8')}
														placeholder="Linux Package Name"
														id="packages"
														name="packages"
														type="text"
														value={pkgRef.current && pkgRef.current.value ? pkgRef.current.value : ''}
														onChange={handleChange}
														fullWidth
													/>
													<Divider className="h-28 m-4" orientation="vertical" />
													<Button
														className="whitespace-no-wrap"
														id="add-pkg-btn"
														disabled={Boolean((pkgRef.current && !pkgRef.current.value) || !pkgRef.current)}
														onClick={setPackagesArr}
													>
														Add
													</Button>
												</Paper>
											</div>
											<div className="flex flex-col items-center justify-center w-1/2 ml-8">
												{form.data.packages.length === 0 ? (
													<div className="flex items-center justify-center h-full w-3/5">
														<Typography id="no-data-files-msg" color="textSecondary" variant="h6">
															No Packages
														</Typography>
													</div>
												) : (
													<Paper className="w-full">
														<Table size="small">
															<TableHead>
																<TableRow>
																	<TableCell id="package-name">Name</TableCell>
																	<TableCell id="package-blank"></TableCell>
																	<TableCell id="package-actions">Actions</TableCell>
																</TableRow>
															</TableHead>
															<TableBody>
																{form.data.packages.map((n, i) => {
																	return (
																		<TableRow key={i}>
																			<TableCell id={`${n}-name`} className="w-2/5">
																				{n}
																			</TableCell>
																			<TableCell id={`${n}-blank`} className="w-2/5"></TableCell>
																			<TableCell id={`${n}-actions`} className="w-1/5">
																				<div className="flex items-center">
																					<IconButton
																						id={`${n}-actions-delete`}
																						onClick={() => handleDeletePackages(n)}
																					>
																						<Icon>delete</Icon>
																					</IconButton>
																				</div>
																			</TableCell>
																		</TableRow>
																	);
																})}
															</TableBody>
														</Table>
													</Paper>
												)}
											</div>
										</div>
									</CardContent>
								</Card>
							)}
						</Formsy>
					</DisabledFormWrapper>
					</DialogContent>
				</React.Fragment>
			)}
            <ScriptDialog
                open={isScriptOpen}
                onClose={() => setScriptOpen(false)}
                scriptContent={form?.data?.provision?.script || ''}
            />
		</React.Fragment>
	);
};

export default MaasCluster;
